import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ApiError,
  useAccountDatabases,
  aiApi,
  aiModelsApi,
  mediaApi,
  slugify,
  useAiKeys,
  useCreateSite,
  type AiProviderId,
} from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Link2, Paperclip, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ModelPicker } from '@/components/baxter/ModelPicker';
import { AiSettingsDialog } from '@/components/baxter/AiSettingsDialog';
import { writeBaxterBrief, type BaxterBrief, type BaxterBriefMedia } from '@/components/baxter/baxterBrief';
import { markGetStartedActive } from '@/components/GetStartedChecklist';
import { pickFilesDesktop } from '@/utils/desktopActions';
import { buildBrief, modelProvider, FREE_MODEL, SITE_INTERVIEWS } from './siteBrief';
import { SiteInterview, answerText, type InterviewAnswer } from './SiteInterview';
import { requestMagic } from '@/components/baxter/magic';

export function validSourceUrl(value: string): boolean {
  try { const url = new URL(value.trim()); return ['http:', 'https:'].includes(url.protocol) && !!url.hostname && !url.username && !url.password; }
  catch { return false; }
}

const ATTACH_ACCEPT = 'image/*,.pdf';

/**
 * AB#642: the AI path on New Site — one centered column, Lovable-style.
 *
 * Name first (the site is created for real before Baxter starts, so the agent
 * has templates to edit), then the brief, then skill/model/attachments. The
 * clarifying questions are Baxter's job in chat: this hands off ONE message.
 *
 * `port` = "Bring a site over": the brief leads with a preview link (base44,
 * Lovable, v0) and the description becomes optional notes.
 */
export function AiSiteWizard({ port, onBusyChange }: { port?: boolean; onBusyChange?: (busy: boolean) => void }) {
  const navigate = useNavigate();
  const createSite = useCreateSite();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const [databaseId, setDatabaseId] = useState('internal');
  const databasesQuery = useAccountDatabases();
  const databases = databasesQuery.data?.data ?? [];
  const [creationUncertain, setCreationUncertain] = useState(false);
  const createdSite = useRef<string | null>(null);
  const buildingRef = useRef(false);
  const uploadedFiles = useRef(new Map<File, BaxterBriefMedia | null>());
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [skill, setSkill] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [building, setBuilding] = useState(false);
  // Provider we need a key for before we can build (opens the key dialog).
  const [interviewing, setInterviewing] = useState(false);
  const [answers, setAnswers] = useState<Record<string, InterviewAnswer>>({});
  const [needsKeyFor, setNeedsKeyFor] = useState<AiProviderId | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const skillsQuery = useQuery({ queryKey: ['ai-skills'], queryFn: () => aiApi.listSkills() });
  // AB#640: only site generators belong in a new-site wizard (not SEO).
  const skills = (skillsQuery.data ?? []).filter((s) => s.generator !== false);
  const modelsQuery = useQuery({ queryKey: ['ai-models', null], queryFn: () => aiModelsApi.list() });
  const models = modelsQuery.data ?? [];
  const keysQuery = useAiKeys();
  const keyedProviders = useMemo(
    () => (keysQuery.data?.data ?? []).map((k) => k.provider as string),
    [keysQuery.data],
  );

  // Uncontrolled-until-touched: the defaults depend on queries that land late.
  const defaultSkill = port ? 'port' : 'love';
  const activeSkill =
    skill ?? (skills.find((s) => s.id === defaultSkill) ?? skills[0])?.id ?? defaultSkill;
  // The server's SSRF guard has the final say — this is just "looks like a link".
  const isPort = activeSkill === 'port';
  const urlOk = !isPort || validSourceUrl(url);
  const activeSlug = slug ?? slugify(name);
  const slugOk = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(activeSlug);
  // Baxter Auto (Claude, WordPuppi pays) unless the person picks another model.
  const activeModel = model ?? FREE_MODEL;
  const activeProvider = modelProvider(activeModel);
  const activeTagline = skills.find((s) => s.id === activeSkill)?.tagline;
  // Port runs in one go; the other generators ask their questions here, one at a time.
  const interview = isPort ? undefined : SITE_INTERVIEWS[activeSkill];

  // Thumbnails for the picked images; revoked when the list changes/unmounts.
  const previews = useMemo(
    () => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [files],
  );
  useEffect(() => () => previews.forEach((url) => url && URL.revokeObjectURL(url)), [previews]);

  const addFiles = (picked: File[]) => setFiles((prev) => [...prev, ...picked]);

  /** A BYOK key is required: without one for the chosen provider we create
   * NOTHING — ask for the key first, then let them press Build again. */
  const keyMissing = () => {
    if (activeModel === FREE_MODEL || keyedProviders.includes(activeProvider)) return false;
    setNeedsKeyFor(activeProvider as AiProviderId);
    return true;
  };

  const start = () => {
    if (buildingRef.current || creationUncertain || !name.trim() || !urlOk || !slugOk || keyMissing()) return;
    if (interview && !createdSite.current) setInterviewing(true);
    else void build();
  };

  const build = async () => {
    if (buildingRef.current || creationUncertain || !name.trim() || !urlOk || !slugOk || keyMissing()) return;

    buildingRef.current = true;
    setBuilding(true);
    onBusyChange?.(true);
    try {
      const database = databases.find((db) => db.id.toString() === databaseId);
      if (databaseId !== 'internal' && !database) throw new Error('Choose an available database.');
      if (!createdSite.current) {
        const result = await createSite.mutateAsync({
          name: name.trim(), slug: activeSlug,
          db_type: database?.db_type ?? 'sqlite',
          ...(database ? { database_connection_id: database.id } : {}),
        });
        if (!result.data?.slug) throw new Error('Site creation could not be confirmed. Check your Sites list before trying again.');
        createdSite.current = result.data.slug;
      }
      const newSlug = createdSite.current;

      const media: BaxterBriefMedia[] = [];
      for (const file of files) {
        const previous = uploadedFiles.current.get(file);
        if (uploadedFiles.current.has(file)) { if (previous) media.push(previous); continue; }
        // Never re-upload an ambiguous failed request when continuing this shell.
        uploadedFiles.current.set(file, null);
        try {
          const uploaded = await mediaApi.upload(newSlug, file, 'baxter');
          if (uploaded.data) {
            const item = { filename: uploaded.data.filename, url: uploaded.data.url };
            uploadedFiles.current.set(file, item);
            media.push(item);
          }
        } catch {
          toast.error(`Couldn't upload ${file.name} — building without it`);
        }
      }

      const brief: BaxterBrief = {
        skill: activeSkill,
        model: activeModel,
        message: buildBrief({
          name, description, media, ...(isPort ? { url } : {}),
          answers: (interview ?? []).map((q) => ({ question: q.question, answer: answerText(q, answers[q.id]) })),
        }),
        media,
      };
      if (!writeBaxterBrief(newSlug, brief)) {
        toast.warning('Browser storage is unavailable. Baxter will receive this brief now; keep the page open until the chat starts.');
      }

      const style = interview?.find((q) => q.id === 'style');
      const styleAnswer = style ? answerText(style, answers[style.id]).replace(/^Let Baxter choose,?\s*/, '') : '';
      requestMagic(newSlug, { name: name.trim(), style: styleAnswer });
      markGetStartedActive(newSlug);
      await navigate({ to: '/sites/$siteSlug/posts', params: { siteSlug: newSlug } });
    } catch (err) {
      // Client timeout (408) can arrive after the server created the shell.
      // Retrying must not create another site after an ambiguous write.
      if (!createdSite.current && !(err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 408)) setCreationUncertain(true);
      toast.error(err instanceof Error ? err.message : 'Failed to create site');
      setInterviewing(false); // back to the form, where the uncertain-creation notice lives
    } finally {
      setBuilding(false);
      buildingRef.current = false;
      onBusyChange?.(false);
    }
  };

  if (interviewing && interview) {
    return (
      <SiteInterview
        questions={interview}
        answers={answers}
        onAnswer={(id, answer) => setAnswers((prev) => ({ ...prev, [id]: answer }))}
        onBack={() => setInterviewing(false)}
        onDone={() => void build()}
        busy={building}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="ai-site-name">Site name</Label>
        <Input
          id="ai-site-name"
          disabled={!!createdSite.current || building}
          data-testid="ai-wizard-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Site name"
          aria-label="Site name"
          required
          autoFocus
        />
        <Label htmlFor="ai-site-slug" className="mt-3">Slug</Label>
        <Input id="ai-site-slug" value={activeSlug} onChange={(e) => setSlug(e.target.value)} disabled={!!createdSite.current || building}
          required pattern="[a-z0-9]+(-[a-z0-9]+)*" aria-describedby="ai-slug-help" />
        <p id="ai-slug-help" className="text-xs text-muted-foreground">Use lowercase letters, numbers and hyphens.</p>
      </div>

      {isPort && (
        <div className="space-y-1">
          <Input
            data-testid="ai-wizard-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-app.base44.app/"
            aria-label="Link to your preview site"
            required
          />
          <p className="pl-1 text-xs text-muted-foreground">
            Paste the link to your preview site — Baxter rebuilds it here.
          </p>
        </div>
      )}

      <div className="space-y-3 rounded-xl border border-border bg-card p-3">
        <Textarea
          data-testid="ai-wizard-brief"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
          aria-label={isPort ? 'Notes for Baxter (optional)' : 'Describe the site you want'}
          placeholder={
            isPort
              ? 'Notes for Baxter (optional) — anything to change, drop or keep'
              : 'A portfolio for a ceramicist in Lisbon with a shop page and a contact form'
          }
          className="resize-none border-none bg-transparent px-1 shadow-none focus-visible:outline-none!"
        />

        <div className="flex flex-wrap items-center gap-1.5">
          {skills.map((s) => (
            <Button
              key={s.id}
              type="button"
              size="sm"
              variant={s.id === activeSkill ? 'default' : 'outline'}
              data-testid={`ai-skill-${s.id}`}
              onClick={() => setSkill(s.id)}
            >
              {s.name}
            </Button>
          ))}

          <div className="ml-auto flex items-center gap-1">
            <ModelPicker models={models} value={activeModel} onChange={setModel} disabled={building} />

            <input
              ref={fileInput}
              data-testid="ai-wizard-files"
              type="file"
              multiple
              accept={ATTACH_ACCEPT}
              className="hidden"
              onChange={(e) => {
                addFiles([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              title="Logo, photos, a brief…"
              // AB#635: on desktop the webview must never open a native panel.
              onClick={(e) => {
                if (!pickFilesDesktop(e, { multiple: true, accept: ATTACH_ACCEPT }, addFiles))
                  fileInput.current?.click();
              }}
            >
              <Paperclip /> Attach
            </Button>
          </div>
        </div>

        {activeTagline && <p className="px-1 text-xs text-muted-foreground">{activeTagline}</p>}
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, i) => (
            <span
              key={`${file.name}-${i}`}
              className="flex items-center gap-2 rounded-md border border-border bg-card py-1 pl-1 pr-2 text-xs"
            >
              {previews[i] ? (
                <img src={previews[i]!} alt="" className="size-8 rounded object-cover" />
              ) : null}
              <span className="max-w-40 truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <details className="rounded-lg border px-4 py-3">
        <summary className="cursor-pointer text-sm font-medium">Advanced options</summary>
        <Label htmlFor="ai-site-database" className="mt-4 mb-2">Database</Label>
        <select id="ai-site-database" className="h-9 w-full rounded-md border bg-background px-3 text-sm" value={databaseId}
          disabled={building || !!createdSite.current || databasesQuery.isLoading} onChange={(e) => setDatabaseId(e.target.value)}>
          <option value="internal">Internal Built-in</option>
          {databases.map((db) => <option key={db.id} value={db.id}>{db.name} ({db.db_type})</option>)}
        </select>
        <p className="mt-2 text-xs text-muted-foreground">Built-in storage is ready to use. You can migrate this site later.</p>
      </details>
      {creationUncertain && <div role="alert" className="space-y-2 text-sm"><p className="text-destructive">Site creation could not be confirmed. Check your Sites list before creating another site.</p><Button type="button" variant="outline" onClick={() => void navigate({ to: '/sites' })}>Check Sites</Button></div>}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          data-testid="ai-wizard-build"
          disabled={!name.trim() || !urlOk || !slugOk || building || creationUncertain}
          onClick={start}
        >
          {isPort ? <Link2 /> : <Sparkles />}
          {building ? 'Preparing your site…' : createdSite.current ? 'Continue to Baxter' : isPort ? 'Bring it over' : interview ? 'Continue' : 'Build it'}
        </Button>
        <p className="text-xs text-muted-foreground">
          {activeModel === FREE_MODEL
            ? 'Baxter Auto picks the right model for each step. WordPuppi covers it.'
            : 'Your own API key runs this model.'}
        </p>
      </div>

      {needsKeyFor && (
        <AiSettingsDialog
          nudge="strong-model"
          defaultProvider={needsKeyFor}
          open
          onOpenChange={(open) => {
            if (open) return;
            setNeedsKeyFor(null);
            void keysQuery.refetch();
          }}
        />
      )}
    </div>
  );
}
